import * as yup from 'yup';
import { i18n } from '@/i18n';
import { yupTransformDate } from '@/helpers';

const t = i18n.global.t;

const translations = {
    definition: {
        required: t('validations.messages.required', {
            _field_: t('delivery.create.definition.label')
        }),
        oneOf: t('validations.messages.oneOf', {
            _field_: t('delivery.create.definition.label')
        })
    },
    versionDate: {
        required: t('validations.messages.required', {
            _field_: t('delivery.create.versionDate.label')
        })
    },
    temporalLimitationFrom: {
        required: t('validations.messages.required', {
            _field_: t('delivery.create.temporalLimitation.from.label')
        })
    },
    versionDescription: {
        required: t('validations.messages.required', {
            _field_: t('delivery.create.description.label')
        })
    },
    file: {
        required: t('validations.messages.required', {
            _field_: t('delivery.create.fileUpload.label')
        }),
        fileType: t('validations.messages.invalidFileType'),
        fileSize: t('validations.messages.invalidFileSize')
    },
    form: {
        uploadFailed: t('deliver.create.uploadfailed')
    }
};

const createFileSchema = ({ required }: { required: boolean }) => {
    let schema: yup.MixedSchema<any, yup.AnyObject, any, ''>;
    if (required) {
        schema = yup
            .mixed()
            .required(translations.file.required)
            .test('is-valid-type', translations.file.fileType, (value) => {
                return (
                    value &&
                    ((value as File).type === 'application/x-zip-compressed' ||
                        (value as File).type === 'application/zip')
                );
            })
            .test(
                'is-valid-size',
                translations.file.fileSize,
                (value) => value && (value as File).size <= 750 * 1000 * 1000
            );
    } else {
        schema = yup.mixed().nullable();
    }

    return schema;
};

const createProductSchema = () => {
    const schema = yup
        .object()
        .shape({
            versionDate: yup
                .date()
                .nullable()
                .transform(yupTransformDate)
                .when('specification', {
                    is: (specification) => !specification.skipMetadataPublication,
                    then: (s) => s.required(translations.versionDate.required),
                    otherwise: (s) => s.optional()
                }),
            temporalLimitationFrom: yup
                .date()
                .nullable()
                .transform(yupTransformDate)
                .when('specification', {
                    is: (specification) => !specification.skipMetadataPublication,
                    then: (s) => s.required(translations.temporalLimitationFrom.required),
                    otherwise: (s) => s.optional()
                }),
            temporalLimitationTo: yup.date().nullable().transform(yupTransformDate),
            versionDescription: yup
                .string()
                .nullable()
                .when('specification', {
                    is: (specification) => !specification.skipMetadataPublication,
                    then: (s) => s.required(translations.versionDescription.required),
                    otherwise: (s) => s.optional()
                })
                .max(
                    100,
                    t('validations.messages.maxChar', {
                        _field_: t('delivery.create.description.label'),
                        length: 100
                    })
                ),
            specification: yup.object().shape({
                id: yup.number().required(),
                label: yup.string().required(),
                skipMetadataPublication: yup.boolean().required()
            })
        })
        .strict()
        .required();

    return schema;
};

export const createValidationSchema = ({ fileRequired = true }: { fileRequired?: boolean } = {}) => {
    return yup.object({
        file: createFileSchema({ required: fileRequired }),
        products: yup.array().of(createProductSchema())
    });
};
